import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  hasSearchCriteria,
  parseCatalogSearch,
  type CatalogSearchParams,
} from '@/features/data/catalog/catalog-api';
import { CatalogChildrenList } from '@/features/data/catalog/catalog-list';
import { CatalogSearchBar, CatalogSearchResults } from '@/features/data/catalog/catalog-search';
import { ConsoleShell } from '@/features/shell/console-shell';

/**
 * Catalogue — les catalogues (couches du contrat), et la recherche des tables (`GET /tables`, lot
 * L07 ; facettes, colonnes et tables disparues le 2026-10-19). Tous les critères vivent dans l'URL
 * (`?search=&column=&tag=&owner=…`) : un lien ou une capture pointe sur un résultat filtré.
 */
export const Route = createFileRoute('/$tenant/catalog/')({
  validateSearch: (search: Record<string, unknown>): CatalogSearchParams => parseCatalogSearch(search),
  component: CatalogRoute,
});

function CatalogRoute() {
  const { tenant } = Route.useParams();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation();
  const onChange = (next: CatalogSearchParams) => void navigate({ search: next, replace: true });
  return (
    <ConsoleShell tenantId={tenant} activeItem="catalog" crumbs={[{ label: t('nav.items.catalog') }]}>
      <CatalogSearchBar params={params} onChange={onChange} />
      {hasSearchCriteria(params) ? (
        <CatalogSearchResults tenantId={tenant} params={params} onChange={onChange} />
      ) : (
        <CatalogChildrenList
          tenantId={tenant}
          parent={undefined}
          title={t('data.catalog.title')}
          description={t('data.catalog.subtitle', { tenant })}
        />
      )}
    </ConsoleShell>
  );
}
